import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, Row, Table, Typography } from 'antd'
import Chart from 'react-apexcharts'

import dayjs from '../../lib/datetime'
import ChartTableSwitch from './ChartTableSwitch'


const { Title } = Typography

export default function PatientCreationStats({ stats }) {
  const [viewMode, setViewMode] = useState('chart')

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'inherit',
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: 'straight',
      width: 2.5,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.05,
        stops: [0, 95, 100],
      },
    },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
    },
    markers: {
      size: 4,
      strokeWidth: 2,
      strokeColors: '#fff',
      hover: { size: 6 },
    },
    xaxis: {
      categories: stats.map(stat =>
        dayjs(`${stat.year}-${String(stat.month).padStart(2, '0')}-01`).format('MMM YY'),
      ),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: -45,
        rotateAlways: true,
        style: { colors: '#94a3b8', fontSize: '11px' },
      },
    },
    yaxis: {
      labels: {
        formatter: value => Math.round(value).toLocaleString(),
        style: { colors: '#94a3b8', fontSize: '11px' },
      },
      min: 0,
    },
    colors: ['#3b82f6'],
    tooltip: {
      y: {
        formatter: value => `${Math.round(value).toLocaleString()} patients`,
      },
    },
  }), [stats])

  const chartSeries = [{
    name: 'Patients Created',
    data: stats.map(stat => stat.count),
  }]

  const tableColumns = [
    {
      key: 'date',
      title: 'Date',
      render: ({ year, month }) =>
        dayjs(`${year}-${String(month).padStart(2, '0')}-01`).format('MMMM YYYY'),
    },
    {
      key: 'count',
      title: 'Patient Count',
      dataIndex: 'count',
      align: 'center',
      render: value => value.toLocaleString(),
    },
  ]

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          {/* level 2 for heading order; the inline style pins the visual size */}
          <Title
            level={2}
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: '#0f172a',
              letterSpacing: '-0.005em',
            }}
          >
            Patients created
          </Title>
        </Col>
        <Col>
          <ChartTableSwitch onChange={setViewMode} viewMode={viewMode} />
        </Col>
      </Row>

      {viewMode === 'chart' && (
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="area"
          height={300}
        />
      )}

      {viewMode === 'table' && (
        <Table
          columns={tableColumns}
          dataSource={stats.map((stat, idx) => ({ ...stat, key: idx }))}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      )}
    </>
  )
}

PatientCreationStats.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ).isRequired,
}
